/**
 * Pingo — click-outside-to-close for every <theme-drawer> on desktop.
 *
 * THE GAP THIS FILLS
 * Horizon already has an outside-click handler, but it is bound to the PANEL:
 *   panel.addEventListener('click', this.#onBackdropClick)   // theme-drawer.js
 * and the drawer picks its dialog mode by breakpoint (MODAL_BREAKPOINT = 990):
 *   < 990px  -> panel.showModal()  — a modal <dialog> paints a ::backdrop, and a
 *               click on that backdrop RETARGETS to the dialog element itself, so
 *               the panel-bound listener fires and closes. Mobile already works.
 *   >= 990px -> panel.show()       — a NON-modal dialog paints no backdrop at all.
 *               An outside click lands on page content, never on the panel, so the
 *               listener is never reached and the drawer stays open.
 * Non-modal is deliberate upstream (it lets the drawer squeeze page content rather
 * than overlay it), so this adds the missing dismiss instead of changing the mode.
 *
 * NATIVE FILE UNTOUCHED (CLAUDE.md rule 9). `theme-drawer` dispatches
 * `theme-drawer:open` / `theme-drawer:close` with bubbles:true and exposes a public
 * async close(), so the whole behaviour lives out here.
 *
 * Universal by owner request (2026-07-15): every <theme-drawer>, not just the cart.
 */

(function () {
  if (window.__pingoDrawerDismissInit) return;
  window.__pingoDrawerDismissInit = true;

  // MUST track MODAL_BREAKPOINT in assets/theme-drawer.js. If upstream changes it,
  // change it here too — a mismatch leaves a viewport band where neither the native
  // backdrop nor this handler closes the drawer.
  var MODAL_BREAKPOINT = 990;

  /** @type {Map<Element, Function>} drawer -> its armed document listener */
  var armed = new Map();

  function isModalMode() {
    return window.matchMedia('(max-width: ' + (MODAL_BREAKPOINT - 1) + 'px)').matches;
  }

  function disarm(drawer) {
    var handler = armed.get(drawer);
    if (!handler) return;
    document.removeEventListener('click', handler);
    armed.delete(drawer);
  }

  function arm(drawer) {
    if (armed.has(drawer)) return;

    var panel = drawer.querySelector('dialog');
    if (!panel) return;

    var handler = function (event) {
      // The drawer may have closed by another route (Esc, close button) without us
      // seeing the event — never leak a listener.
      if (!drawer.hasAttribute('open')) {
        disarm(drawer);
        return;
      }

      // Below the breakpoint the native backdrop owns dismissal. Checked HERE rather
      // than at arm-time so that resizing across 990px while the drawer is open hands
      // off correctly in both directions.
      if (isModalMode()) return;

      var target = event.target;
      if (!(target instanceof Node)) return;

      // Inside the drawer — not an outside click.
      if (panel.contains(target)) return;

      // Mirrors native's #hasOpenNestedDialog() guard (theme-drawer.js:109): while a
      // dialog is open inside the drawer (quick-add, size chart…), an outside click
      // belongs to that dialog's own dismissal, not ours.
      if (panel.querySelector('dialog[open]')) return;

      // A dialog OUTSIDE the drawer's subtree (anything teleported to <body>) owns
      // its own clicks. Without this, clicking inside such a modal would close the
      // drawer underneath it.
      var owningDialog = target instanceof Element ? target.closest('dialog[open]') : null;
      if (owningDialog && owningDialog !== panel) return;

      drawer.close();
    };

    // Deferred one tick ON PURPOSE. open() is called from a click handler, and that
    // click is still propagating toward document. Registering synchronously means
    // this listener fires for the very click that opened the drawer, sees it landed
    // outside the panel, and closes it instantly — the drawer would never appear.
    window.setTimeout(function () {
      if (!drawer.hasAttribute('open')) return; // closed again before we armed
      document.addEventListener('click', handler);
      armed.set(drawer, handler);
    }, 0);
  }

  document.addEventListener('theme-drawer:open', function (event) {
    var drawer = event.target;
    if (drawer instanceof HTMLElement && drawer.matches('theme-drawer')) arm(drawer);
  });

  document.addEventListener('theme-drawer:close', function (event) {
    var drawer = event.target;
    if (drawer instanceof HTMLElement) disarm(drawer);
  });
})();
